import { useState } from 'react';

import BpkBadge, {
  BADGE_TYPES,
} from '@skyscanner/backpack-web/bpk-component-badge';
import BpkCarousel from '@skyscanner/backpack-web/bpk-component-carousel';
import BpkText, {
  TEXT_STYLES,
} from '@skyscanner/backpack-web/bpk-component-text';
import { cssModules } from '@skyscanner/backpack-web/bpk-react-utils';

import ImageItem from '../ImageItem';

import type { I18nService } from '@shared/types/i18n';
import type { UserContext } from '@shared/types/user-context';

import STYLES from './MediaSlider.module.scss';

import {
  useAllImages,
  useHotelImagesContext,
} from '@hotels-hotel-detail/data-access/hooks/useHotelImagesContext';
import { VIDEOS_CATEGORY } from '@hotels-hotel-detail/utils/hotel-images';
import { buildDetailsImageGalleryAttributes } from '@hotels-hotel-detail/utils/meaningful-behaviour-data/hotel-details-image-gallery';
import { withI18n } from '@shared/ui/contexts/i18n';
import { withUserContext } from '@shared/utils-frontend/contexts/UserContext';

const cls = cssModules(STYLES);

type Props = {
  i18n: I18nService;
  userContext: UserContext;
  hotelId: string;
};

const MediaSlider = ({
  hotelId,
  i18n,
  userContext: { enableSEOVariantHotelsDetailsPage },
}: Props) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const { allImages, imagesCount } = useAllImages();
  const { openGallery } = useHotelImagesContext();

  if (!allImages || allImages.length === 0) {
    return null;
  }

  const imagesNode = allImages.map(({ category, thumbnail }, imageIndex) => {
    const imageMBDProps = buildDetailsImageGalleryAttributes({
      hotelId,
      sortId: imageIndex,
    });

    return (
      <ImageItem
        key={thumbnail}
        src={thumbnail}
        imageIndex={currentIndex}
        mbdProps={imageMBDProps}
        showVideoPlayOverlay={category === VIDEOS_CATEGORY}
        lazyLoad={imageIndex !== 0}
        openGallery={openGallery}
      />
    );
  });

  return (
    <div
      className={cls(
        'MediaSlider',
        enableSEOVariantHotelsDetailsPage && 'MediaSlider__mobileHeaderSeo',
      )}
    >
      <BpkCarousel
        images={imagesNode}
        onImageChanged={(index) => setCurrentIndex(index)}
        bottom={36}
      />
      <BpkBadge
        type={BADGE_TYPES.inverse}
        className={cls('MediaSlider__imageCount')}
      >
        <BpkText textStyle={TEXT_STYLES.footnote}>
          {i18n.translate('HotelImageGallery_label_imageNumbering', {
            current: currentIndex + 1,
            total: imagesCount,
          })}
        </BpkText>
      </BpkBadge>
    </div>
  );
};

export default withUserContext(withI18n(MediaSlider));
